// BracePointsDialog.tsx — Up to 8 brace insert locations (bpvar)

import DialogBase from './DialogBase';
import { SlotEditor } from './SlotEditor';
import { usePendingSlot } from './usePendingSlot';
import { useShallowStore } from '../store/useStore';
import { EmbedPalette } from '../utils/embedLibrary';
import type { EmbedTypeEntry } from '../utils/embedLibrary';
import type { BpData } from '../types/panelvar';

const FIELDS = [
  { key: 'mark', label: 'Mark', type: 'text'   as const },
  { key: 'x',    label: 'X',    type: 'number' as const, unit: '"', min: 0 },
  { key: 'y',    label: 'Y',    type: 'number' as const, unit: '"', min: 0 },
  { key: 'size', label: 'Size', type: 'number' as const, unit: '"', min: 0, step: 0.125 },
];

const BP_DEFAULT: BpData = { mark: 'BP', x: 0, y: 0, type: 'strongback', embedLen: 12, size: 1.25 };

export default function BracePointsDialog() {
  const { ui, project, updatePanel } = useShallowStore(s => ({
    ui: s.ui, project: s.project, updatePanel: s.updatePanel,
  }));
  const panel = project?.panels.find(p => p.id === ui.activePanelId);
  const [slots, setSlots] = usePendingSlot<BpData>('bpvar', panel?.bpvar ?? [],
    (px, py, mark) => ({ mark, x: px, y: py }),
    { mark: 'BP', x: 0, y: 0, type: 'strongback' as const, embedLen: 12, size: 1.25 });
  if (!panel) return null;

  function addFromPalette(entry: EmbedTypeEntry) {
    const data: BpData = { ...BP_DEFAULT, ...(entry.data as Partial<BpData>) };
    setSlots(prev => {
      const idx = prev.findIndex(s => !s.enabled);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { enabled: true, data };
        return next;
      }
      if (prev.length < 8) return [...prev, { enabled: true, data }];
      return prev;
    });
  }

  return (
    <DialogBase title="Brace Points (8 max)" onOk={() => updatePanel(panel.id, { bpvar: slots })}>
      <EmbedPalette category="brace" settings={project?.settings} onSelect={addFromPalette} />
      <SlotEditor
        slots={slots}
        fields={FIELDS}
        maxSlots={8}
        onChange={v => setSlots(v)}
        defaultData={() => ({ mark: 'BP', x: 0, y: 0, type: 'strongback' as const, embedLen: 12, size: 1.25 })}
        label="BP"
      />
    </DialogBase>
  );
}
